import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { POLYGON_API_KEY } from "../../config";
import Api from "../../service/http";
import SelectTicker from "./components/SelectTicker";
import { MarketDataDelaySubscribeInline } from "./components/MarketDataDelaySubscribeHeader";
import useMarketDataSubscription from "./hooks/useMarketDataSubscription";
import TickerLimitBanner from "./components/TickerLimitBanner";
import Loading from "../../components/Loading";
import {
  formatTickerApiLimitMessage,
  isTickerApiLimitResponse,
} from "../../utils/tickerApiLimit";
import { HiOutlineGlobeAlt, HiOutlinePhone, HiOutlineOfficeBuilding, HiOutlineCurrencyDollar, HiOutlineSearch, HiOutlineDocumentText } from "react-icons/hi";

const Details = ({ selected, setSelected, isActive }) => {
  const { theme } = useSelector((state) => state.theme);
  const subscribed = useMarketDataSubscription();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const loadedTickerRef = useRef(null);
  const atWeeklyLimitRef = useRef(false);

  const getData = async () => {
    if (!selected || selected === "all") {
      setData(null);
      setLoading(false);
      setFetchError(null);
      return;
    }

    const showLoader = !atWeeklyLimitRef.current;
    if (showLoader) {
      setLoading(true);
      setFetchError(null);
    }
    try {
      const res = await Api.get("/app/marketdata/ticker-details/" + selected);

      if (isTickerApiLimitResponse(res?.data, res?.status)) {
        atWeeklyLimitRef.current = true;
        setFetchError(formatTickerApiLimitMessage(res.data));
        setData(null);
        setLoading(false);
        return;
      }

      if (res?.data?.status === "error") {
        setFetchError({
          headline: res.data.message || "Could not load ticker details.",
          detail: "Please try again or pick another symbol.",
        });
        setData(null);
        setLoading(false);
        return;
      }

      const response = res?.data;
      atWeeklyLimitRef.current = false;
      setData(response?.data?.results || []);
      loadedTickerRef.current = selected;
    } catch (error) {
      console.error("Failed to fetch ticker details", error);
      const payload = error?.response?.data;
      const status = error?.response?.status;
      if (isTickerApiLimitResponse(payload, status)) {
        atWeeklyLimitRef.current = true;
        setFetchError(formatTickerApiLimitMessage(payload));
      } else {
        setFetchError({
          headline: payload?.message || "Could not load ticker details.",
          detail: "Check your connection and try again.",
        });
      }
      setData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!selected || selected === "all" || !isActive) return;
    if (loadedTickerRef.current === selected && data != null) return;
    getData();
  }, [selected, isActive]);

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Top Header/Search Bar */}
      <div className={`border-b p-3 sm:p-4 ${theme === 'dark' ? 'border-slate-800 bg-slate-900/50' : 'border-slate-100 bg-slate-50/50'}`}>
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className={`shrink-0 rounded-lg p-2 ${theme === 'dark' ? 'bg-violet-500/10 text-violet-400' : 'bg-violet-100 text-violet-600'}`}>
              <HiOutlineDocumentText size={22} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1">
              <h2 className={`text-base font-bold sm:text-lg ${theme === 'dark' ? 'text-slate-200' : 'text-slate-900'}`}>Ticker Details</h2>
              {!subscribed && <MarketDataDelaySubscribeInline theme={theme} />}
            </div>
          </div>
          <SelectTicker selected={selected} setSelected={setSelected} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-5">
        <div className="max-w-5xl mx-auto">
          {!selected || selected === "all" ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'}`}>
                  <HiOutlineSearch className="text-slate-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-2">No Ticker Selected</h3>
              <p className="text-slate-500 max-w-xs">Search and select a ticker symbol above to view its detailed market profile.</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <Loading message="Fetching company profile..." />
            </div>
          ) : fetchError ? (
            <div className="py-8">
              <TickerLimitBanner message={fetchError} theme={theme} />
            </div>
          ) : data ? (
            <div className="space-y-4">
              {/* Main Info Card */}
              <div className={`rounded-3xl p-5 sm:p-6 ${theme === 'dark' ? 'bg-slate-800/40 border border-slate-700/50' : 'bg-white border border-slate-100 shadow-sm'}`}>
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  {/* Logo & Basic Info */}
                  <div className="flex flex-col items-center md:items-start space-y-4">
                    {data?.branding?.logo_url ? (
                      <div className={`p-4 rounded-2xl ${theme === 'dark' ? 'bg-white' : 'bg-slate-50 border border-slate-100'}`}>
                        <img
                          src={`${data?.branding?.logo_url}?apiKey=${POLYGON_API_KEY}`}
                          alt={`${data?.name} logo`}
                          className="h-20 w-20 object-contain"
                        />
                      </div>
                    ) : (
                      <div className={`h-28 w-28 rounded-2xl flex items-center justify-center text-4xl font-bold ${theme === 'dark' ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-300'}`}>
                        {data?.ticker?.charAt(0)}
                      </div>
                    )}
                    <div className="text-center md:text-left">
                      <h2 className="text-3xl font-extrabold tracking-tight">
                        {data?.name}
                      </h2>
                      <div className="flex items-center justify-center md:justify-start gap-2 mt-1">
                        <span className="px-2.5 py-0.5 rounded-lg bg-blue-500/10 text-blue-500 text-sm font-bold border border-blue-500/20">
                          {data?.ticker}
                        </span>
                        <span className="text-slate-500 text-sm font-medium">
                          {data?.primary_exchange} • {data?.market?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats Grid */}
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                    <div className={`p-4 rounded-2xl ${theme === 'dark' ? 'bg-slate-900/50' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-3 text-slate-500 mb-1">
                        <HiOutlineCurrencyDollar size={18} />
                        <span className="text-xs font-semibold uppercase tracking-wider">Market Cap</span>
                      </div>
                      <p className="text-xl font-bold">
                        {data?.market_cap ? `$${data.market_cap.toLocaleString()}` : "N/A"}
                      </p>
                    </div>
                    {data?.phone_number && (
                      <div className={`p-4 rounded-2xl ${theme === 'dark' ? 'bg-slate-900/50' : 'bg-slate-50'}`}>
                        <div className="flex items-center gap-3 text-slate-500 mb-1">
                          <HiOutlinePhone size={18} />
                          <span className="text-xs font-semibold uppercase tracking-wider">Contact</span>
                        </div>
                        <p className="text-lg font-bold">{data.phone_number}</p>
                      </div>
                    )}
                    {data?.homepage_url && (
                      <div className={`p-4 rounded-2xl ${theme === 'dark' ? 'bg-slate-900/50' : 'bg-slate-50'} sm:col-span-2`}>
                        <div className="flex items-center gap-3 text-slate-500 mb-1">
                          <HiOutlineGlobeAlt size={18} />
                          <span className="text-xs font-semibold uppercase tracking-wider">Official Website</span>
                        </div>
                        <a
                          href={data.homepage_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-600 font-bold transition-colors break-all"
                        >
                          {data.homepage_url.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {data?.description && (
                  <div className="mt-6 pt-6 border-t border-slate-700/30">
                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">About {data.name}</h3>
                    <p className={`text-base leading-relaxed ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                      {data.description}
                    </p>
                  </div>
                )}
                
                {/* Address */}
                {data?.address && (
                  <div className="mt-6 flex items-start gap-3 text-sm text-slate-500">
                    <HiOutlineOfficeBuilding size={20} className="mt-0.5 shrink-0" />
                    <p>{data?.address?.address1}, {data?.address?.city}, {data?.address?.state} {data?.address?.postal_code}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-slate-500">No profile details available for this ticker.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Details;
